<template>
  <div class="monitor-page">
    <div v-if="!isLoggedIn" class="login-wrap">
      <div class="login-card">
        <h2 class="login-title">监控系统登录</h2>
        <div class="form-item">
          <div class="label">账号</div>
          <Input v-model="loginForm.username" placeholder="请输入账号" @on-enter="doLogin" />
        </div>
        <div class="form-item">
          <div class="label">密码</div>
          <Input v-model="loginForm.password" type="password" placeholder="请输入密码" @on-enter="doLogin" />
        </div>
        <Button type="primary" long :loading="loginLoading || authLoading" @click="doLogin">登录</Button>
      </div>
    </div>

    <div v-else>
    <div class="hero">
      <h1 class="hero-title">网站监控中心</h1>
      <div class="hero-subtitle">实时查看站点可用性、域名到期与 SSL 证书健康状态</div>
    </div>

    <div class="card">
      <div class="title">网站监控总览</div>
      <div class="overview">
        <div class="metric"><div class="name">监控总数</div><div class="value">{{ overview.total || 0 }}</div></div>
        <div class="metric"><div class="name">正常数量</div><div class="value">{{ overview.normal || 0 }}</div></div>
        <div class="metric"><div class="name">待处理数量</div><div class="value">{{ overview.warning || 0 }}</div></div>
        <div class="metric"><div class="name">待检测数量</div><div class="value">{{ overview.pending || 0 }}</div></div>
      </div>
    </div>

    <div class="card">
      <div class="title">新增域名</div>
      <div class="actions">
        <Button type="primary" @click="openAddModal">新增站点</Button>
        <Button type="success" :loading="checkAllLoading" @click="checkAll">立即全量检测</Button>
        <Button @click="logout">退出登录</Button>
      </div>
    </div>

    <div class="card">
      <div class="title">监控列表</div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>网站</th>
              <th>网站状态</th>
              <th>域名到期</th>
              <th>SSL到期</th>
              <th>最后检测</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item.id">
              <td>{{ item.id }}</td>
              <td>
                <div class="site-name">{{ item.domain }}</div>
                <div class="site-url">{{ item.url }}</div>
                <div class="site-remark" v-if="item.remark">备注：{{ item.remark }}</div>
              </td>
              <td>
                <span class="status" :class="statusClassBy(item, 'site')">{{ statusTextBy(item, 'site') }}</span>
                <div class="msg">{{ item.siteMessage }}</div>
              </td>
              <td>
                <span class="status" :class="statusClassBy(item, 'domain')">{{ statusTextBy(item, 'domain') }}</span>
                <div class="msg">{{ item.domainMessage }}</div>
                <div class="msg">{{ item.domainExpireTime || '-' }}</div>
              </td>
              <td>
                <span class="status" :class="statusClassBy(item, 'ssl')">{{ statusTextBy(item, 'ssl') }}</span>
                <div class="msg">{{ item.sslMessage }}</div>
                <div class="msg">{{ item.sslExpireTime || '-' }}</div>
              </td>
              <td class="time-cell">{{ item.lastCheckTime || '-' }}</td>
              <td>
                <Button type="warning" size="small" :loading="checkOneLoadingId === item.id" :disabled="deletingId === item.id" @click="checkOne(item.id)">检测</Button>
                <Button type="primary" size="small" style="margin-left:6px;" :disabled="checkOneLoadingId === item.id || deletingId === item.id" @click="openEditModal(item)">编辑</Button>
                <Button type="error" size="small" style="margin-left:6px;" :loading="deletingId === item.id" :disabled="checkOneLoadingId === item.id" @click="deleteOne(item.id)">删除</Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Modal
      v-model="showAddModal"
      title="新增监控站点"
      :mask-closable="false"
      :loading="addSubmitting"
      @on-ok="addDomain"
      ok-text="确认新增"
      cancel-text="取消">
      <div class="modal-form">
        <div class="form-item">
          <div class="label">域名</div>
          <Input v-model="form.domain" placeholder="请输入域名，如 example.com" />
        </div>
        <div class="form-item">
          <div class="label">站点备注</div>
          <Input v-model="form.remark" placeholder="例如：官网/支付中心/博客（可选）" />
        </div>
        <div class="form-item">
          <div class="label">通知邮箱</div>
          <Input v-model="form.notifyEmail" placeholder="通知邮箱（可选）" />
        </div>
        <div class="form-item">
          <div class="label">到期预警阈值（天）</div>
          <InputNumber :min="1" v-model="form.expireThresholdDays" style="width:100%;" />
        </div>
        <div class="form-item">
          <div class="label">监控项目</div>
          <div class="switch-row">
            <span class="switch-label">网站状态</span>
            <i-switch v-model="form.enableSiteCheck" :true-value="1" :false-value="0" />
            <span class="switch-label">域名到期</span>
            <i-switch v-model="form.enableDomainCheck" :true-value="1" :false-value="0" />
            <span class="switch-label">SSL证书</span>
            <i-switch v-model="form.enableSslCheck" :true-value="1" :false-value="0" />
          </div>
        </div>
      </div>
    </Modal>

    <Modal
      v-model="showEditModal"
      title="编辑监控站点"
      :mask-closable="false"
      :loading="editSubmitting"
      @on-ok="updateDomain"
      ok-text="保存"
      cancel-text="取消">
      <div class="modal-form">
        <div class="form-item">
          <div class="label">域名</div>
          <Input :value="editForm.domain" disabled />
        </div>
        <div class="form-item">
          <div class="label">站点备注</div>
          <Input v-model="editForm.remark" placeholder="例如：官网/支付中心/博客（可选）" />
        </div>
        <div class="form-item">
          <div class="label">通知邮箱</div>
          <Input v-model="editForm.notifyEmail" placeholder="通知邮箱（可选）" />
        </div>
        <div class="form-item">
          <div class="label">到期预警阈值（天）</div>
          <InputNumber :min="1" v-model="editForm.expireThresholdDays" style="width:100%;" />
        </div>
        <div class="form-item">
          <div class="label">监控项目</div>
          <div class="switch-row">
            <span class="switch-label">网站状态</span>
            <i-switch v-model="editForm.enableSiteCheck" :true-value="1" :false-value="0" />
            <span class="switch-label">域名到期</span>
            <i-switch v-model="editForm.enableDomainCheck" :true-value="1" :false-value="0" />
            <span class="switch-label">SSL证书</span>
            <i-switch v-model="editForm.enableSslCheck" :true-value="1" :false-value="0" />
          </div>
        </div>
      </div>
    </Modal>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HelloWorld',
  data () {
    return {
      overview: {},
      list: [],
      isLoggedIn: false,
      authLoading: false,
      loginLoading: false,
      loginForm: {
        username: '',
        password: ''
      },
      showAddModal: false,
      showEditModal: false,
      addSubmitting: false,
      editSubmitting: false,
      checkAllLoading: false,
      checkOneLoadingId: null,
      deletingId: null,
      form: {
        domain: '',
        remark: '',
        notifyEmail: '',
        expireThresholdDays: 30,
        enableSiteCheck: 1,
        enableDomainCheck: 1,
        enableSslCheck: 1
      },
      editForm: {
        id: null,
        domain: '',
        remark: '',
        notifyEmail: '',
        expireThresholdDays: 30,
        enableSiteCheck: 1,
        enableDomainCheck: 1,
        enableSslCheck: 1
      }
    }
  },
  created () {
    this.checkLogin()
  },
  methods: {
    checkLogin () {
      this.authLoading = true
      this.$axios.get('/api/monitor/auth/me').then((res) => {
        this.isLoggedIn = !!(res.data && res.data.success)
        if (this.isLoggedIn) {
          this.reload()
        }
      }).finally(() => {
        this.authLoading = false
      })
    },
    doLogin () {
      if (this.loginLoading) return
      this.loginLoading = true
      const data = this.$qs.stringify({
        username: this.loginForm.username || '',
        password: this.loginForm.password || ''
      })
      this.$axios.post('/api/monitor/auth/login', data).then((res) => {
        this.$Message.info((res.data && res.data.msg) || '完成')
        if (res.data && res.data.success) {
          this.isLoggedIn = true
          this.reload()
        }
      }).finally(() => {
        this.loginLoading = false
      })
    },
    logout () {
      this.$axios.post('/api/monitor/auth/logout').then(() => {
        this.isLoggedIn = false
        this.list = []
        this.overview = {}
      })
    },
    statusTextBy (item, kind) {
      const enabled = this.isEnabled(item, kind)
      if (!enabled) return '关闭'
      const s = this.getStatus(item, kind)
      if (s === 1) return '正常'
      if (s === 2) return '异常'
      if (s === 3) return '警告'
      return '待检测'
    },
    statusClassBy (item, kind) {
      const enabled = this.isEnabled(item, kind)
      if (!enabled) return 'disabled'
      const s = this.getStatus(item, kind)
      if (s === 1) return 'ok'
      if (s === 2) return 'warn-status'
      if (s === 3) return 'caution'
      return 'pending'
    },
    isEnabled (item, kind) {
      if (kind === 'site') return (item.enableSiteCheck === undefined || item.enableSiteCheck === null) ? true : item.enableSiteCheck === 1
      if (kind === 'domain') return (item.enableDomainCheck === undefined || item.enableDomainCheck === null) ? true : item.enableDomainCheck === 1
      return (item.enableSslCheck === undefined || item.enableSslCheck === null) ? true : item.enableSslCheck === 1
    },
    getStatus (item, kind) {
      if (kind === 'site') return item.siteStatus
      if (kind === 'domain') return item.domainStatus
      return item.sslStatus
    },
    reload () {
      this.fetchOverview()
      this.fetchList()
    },
    fetchOverview () {
      this.$axios.get('/api/monitor/overview').then((res) => {
        if (res.data && res.data.success) {
          this.overview = res.data.data || {}
        }
      })
    },
    fetchList () {
      this.$axios.get('/api/monitor/list').then((res) => {
        if (res.data && res.data.success) {
          this.list = res.data.data || []
        }
      })
    },
    openAddModal () {
      this.showAddModal = true
    },
    openEditModal (item) {
      this.editForm.id = item.id
      this.editForm.domain = item.domain || ''
      this.editForm.remark = item.remark || ''
      this.editForm.notifyEmail = item.notifyEmail || ''
      this.editForm.expireThresholdDays = item.expireThresholdDays || 30
      this.editForm.enableSiteCheck = item.enableSiteCheck === undefined || item.enableSiteCheck === null ? 1 : item.enableSiteCheck
      this.editForm.enableDomainCheck = item.enableDomainCheck === undefined || item.enableDomainCheck === null ? 1 : item.enableDomainCheck
      this.editForm.enableSslCheck = item.enableSslCheck === undefined || item.enableSslCheck === null ? 1 : item.enableSslCheck
      this.showEditModal = true
    },
    addDomain () {
      if (this.addSubmitting) return
      this.addSubmitting = true
      const data = this.$qs.stringify({
        domain: this.form.domain || '',
        remark: this.form.remark || '',
        notifyEmail: this.form.notifyEmail || '',
        enableSiteCheck: this.form.enableSiteCheck,
        enableDomainCheck: this.form.enableDomainCheck,
        enableSslCheck: this.form.enableSslCheck,
        expireThresholdDays: this.form.expireThresholdDays || 30
      })
      this.$axios.post('/api/monitor/add', data).then((res) => {
        this.$Message.info((res.data && res.data.msg) || '完成')
        if (res.data && res.data.success) {
          this.showAddModal = false
          this.form.domain = ''
          this.form.remark = ''
          this.form.notifyEmail = ''
          this.form.expireThresholdDays = 30
          this.form.enableSiteCheck = 1
          this.form.enableDomainCheck = 1
          this.form.enableSslCheck = 1
          this.reload()
        }
      }).finally(() => {
        this.addSubmitting = false
      })
    },
    updateDomain () {
      if (this.editSubmitting) return
      this.editSubmitting = true
      const data = this.$qs.stringify({
        id: this.editForm.id,
        remark: this.editForm.remark || '',
        notifyEmail: this.editForm.notifyEmail || '',
        enableSiteCheck: this.editForm.enableSiteCheck,
        enableDomainCheck: this.editForm.enableDomainCheck,
        enableSslCheck: this.editForm.enableSslCheck,
        expireThresholdDays: this.editForm.expireThresholdDays || 30
      })
      this.$axios.post('/api/monitor/update', data).then((res) => {
        this.$Message.info((res.data && res.data.msg) || '完成')
        if (res.data && res.data.success) {
          this.showEditModal = false
          this.reload()
        }
      }).finally(() => {
        this.editSubmitting = false
      })
    },
    checkAll () {
      if (this.checkAllLoading) return
      this.checkAllLoading = true
      this.$Message.info('正在执行全量检测，请稍候...')
      this.$axios.post('/api/monitor/checkAll').then((res) => {
        this.$Message.info((res.data && res.data.msg) || '完成')
        this.reload()
      }).finally(() => {
        this.checkAllLoading = false
      })
    },
    checkOne (id) {
      if (this.checkOneLoadingId !== null) return
      this.checkOneLoadingId = id
      const data = this.$qs.stringify({ id: id })
      this.$axios.post('/api/monitor/checkOne', data).then((res) => {
        this.$Message.info((res.data && res.data.msg) || '完成')
        this.reload()
      }).finally(() => {
        this.checkOneLoadingId = null
      })
    },
    deleteOne (id) {
      if (this.deletingId !== null) return
      this.$Modal.confirm({
        title: '删除确认',
        content: '确认删除该监控站点吗？',
        onOk: () => {
          this.deletingId = id
          const data = this.$qs.stringify({ id: id })
          this.$axios.post('/api/monitor/delete', data).then((res) => {
            this.$Message.info((res.data && res.data.msg) || '完成')
            this.reload()
          }).finally(() => {
            this.deletingId = null
          })
        }
      })
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; }
.monitor-page {
  padding: 16px;
  color: #1f2937;
  background: linear-gradient(180deg, #f0f5ff 0%, #f7f8fc 220px, #f5f7fb 100%);
  min-height: 100vh;
}
.login-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
.login-card {
  width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #edf0f5;
  box-shadow: 0 10px 24px rgba(15, 23, 42, .08);
}
.login-title {
  margin: 0 0 18px;
  font-size: 22px;
  color: #111827;
}
.hero {
  padding: 22px 24px;
  border-radius: 16px;
  margin-bottom: 16px;
  color: #fff;
  background: linear-gradient(120deg, #1677ff 0%, #2f54eb 52%, #722ed1 100%);
  box-shadow: 0 12px 28px rgba(30, 64, 175, .25);
}
.hero-title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
}
.hero-subtitle {
  margin-top: 6px;
  opacity: .92;
  font-size: 14px;
}
.card {
  background: rgba(255,255,255,.95);
  border: 1px solid #edf0f5;
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, .05);
}
.title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 14px;
  color: #111827;
}
.overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px,1fr));
  gap: 12px;
}
.metric {
  border-radius: 12px;
  padding: 14px;
  border: 1px solid #e9edf5;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}
.name {
  color: #667085;
  font-size: 13px;
}
.value {
  font-size: 28px;
  font-weight: 700;
  margin-top: 8px;
  color: #1d4ed8;
}
.form-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 180px 230px;
  gap: 10px;
}
.input {
  width: 100%;
  height: 40px;
  border: 1px solid #d9e0ea;
  background: #fff;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  color: #1f2937;
}
.input:focus {
  outline: none;
  border-color: #4096ff;
  box-shadow: 0 0 0 3px rgba(64,150,255,.15);
}
.actions {
  display: flex;
  gap: 8px;
}
.table-wrap {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid #e9edf5;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: #fff;
  min-width: 980px;
}
th, td {
  border-bottom: 1px solid #eef2f7;
  padding: 12px;
  text-align: left;
  vertical-align: top;
}
th {
  background: #f8fafc;
  color: #4b5563;
  font-weight: 700;
}
tbody tr:hover { background: #f8fbff; }
.site-name { font-weight: 700; color: #0f172a; }
.site-url { font-size: 12px; color: #667085; margin-top: 3px; }
.site-remark { font-size: 12px; color: #334155; margin-top: 3px; }
.status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #fff;
  font-weight: 600;
}
.pending { background: #6b7280; }
.ok { background: #16a34a; }
.warn-status { background: #dc2626; }
.caution { background: #f59e0b; }
.disabled { background: #94a3b8; }
.msg { font-size: 12px; color: #475467; margin-top: 6px; line-height: 1.45; }
.time-cell { color: #475467; font-size: 13px; white-space: nowrap; }

@media (max-width: 980px) {
  .overview { grid-template-columns: repeat(2, minmax(130px,1fr)); }
  .form-grid { grid-template-columns: 1fr; }
}

.modal-form .form-item {
  margin-bottom: 12px;
}
.modal-form .label {
  margin-bottom: 6px;
  color: #374151;
  font-size: 13px;
}
.switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.switch-label {
  font-size: 12px;
  color: #475467;
}
</style>



// WEBPACK FOOTER //
// src/components/HelloWorld.vue